"use client";

import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Eye, EyeOff, Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import MarkdownRenderer from "@/components/common/MarkdownRenderer";
import type { Block } from "@/lib/book-types";
import {
  isChoiceQuizQuestion,
  normalizeQuizQuestionType,
  resolveChoiceAnswerKey,
} from "@/lib/quiz-question-type";
import { assessQualitativeMastery } from "@/lib/learning-api";
import {
  canRevealQuizAnswer,
  isQualitativeAssessEligible,
  qualitativeAssessFailureMessage,
  type QualitativeAssessResponse,
} from "@/lib/qualitative-assess";

export interface QuizAttemptArgs {
  questionId?: string;
  userAnswer?: string;
  isCorrect: boolean;
}

interface QuizQuestion {
  question_id?: string;
  question?: string;
  question_type?: string;
  options?: Record<string, string> | null;
  correct_answer?: string;
  explanation?: string;
  difficulty?: string;
  knowledge_point_id?: string | null;
}

export interface QuizBlockProps {
  block: Block;
  bookId?: string;
  onAttempt?: (block: Block, args: QuizAttemptArgs) => void;
}

export default function QuizBlock({
  block,
  bookId,
  onAttempt,
}: QuizBlockProps) {
  const { t } = useTranslation();
  const questions =
    (block.payload?.questions as QuizQuestion[] | undefined) || [];
  if (questions.length === 0) {
    return (
      <div className="text-sm text-[var(--muted-foreground)]">
        {t("No quiz questions generated.")}
      </div>
    );
  }
  return (
    <section>
      <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--primary)]">
        <span className="h-px flex-1 bg-[var(--primary)]/20" />
        {t("Quick Check")}
        <span className="h-px flex-1 bg-[var(--primary)]/20" />
      </div>
      <div className="space-y-3">
        {questions.map((q, idx) => (
          <QuizQuestionCard
            key={q.question_id || idx}
            index={idx}
            question={q}
            bookId={bookId}
            onAttempt={(args) => onAttempt?.(block, args)}
          />
        ))}
      </div>
    </section>
  );
}

function QuizQuestionCard({
  index,
  question,
  bookId,
  onAttempt,
}: {
  index: number;
  question: QuizQuestion;
  bookId?: string;
  onAttempt?: (args: QuizAttemptArgs) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [assessment, setAssessment] =
    useState<QualitativeAssessResponse | null>(null);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [isAssessing, setIsAssessing] = useState(false);
  const [hasPassedQualitativeAssessment, setHasPassedQualitativeAssessment] =
    useState(false);
  const reportedRef = useRef<string | null>(null);
  const normalizedType = normalizeQuizQuestionType(question.question_type);
  const options = question.options || {};
  const isChoice = isChoiceQuizQuestion(normalizedType);
  const qualitativeKnowledgePointId =
    typeof question.knowledge_point_id === "string"
      ? question.knowledge_point_id.trim()
      : "";
  const questionId =
    typeof question.question_id === "string" ? question.question_id.trim() : "";
  const canRevealAnswer = canRevealQuizAnswer(
    normalizedType,
    qualitativeKnowledgePointId,
    hasPassedQualitativeAssessment,
  );
  const isQualitativeAssessment = isQualitativeAssessEligible(
    normalizedType,
    qualitativeKnowledgePointId,
    bookId,
  );
  const correct = String(question.correct_answer || "").trim();
  const correctChoiceKey = resolveChoiceAnswerKey(correct, options);
  const reportKey = `${question.question_id || index}:${selected || ""}`;

  useEffect(() => {
    if (
      isChoice &&
      revealed &&
      selected &&
      reportedRef.current !== reportKey &&
      onAttempt
    ) {
      reportedRef.current = reportKey;
      onAttempt({
        questionId: question.question_id,
        userAnswer: selected,
        isCorrect: selected.toUpperCase() === correctChoiceKey,
      });
    }
  }, [
    correctChoiceKey,
    isChoice,
    onAttempt,
    question.question_id,
    reportKey,
    revealed,
    selected,
  ]);

  const handleQualitativeSubmit = async () => {
    if (
      !isQualitativeAssessment ||
      !bookId ||
      !qualitativeKnowledgePointId ||
      !questionId
    ) {
      return;
    }

    setIsAssessing(true);
    setAssessment(null);
    setAssessmentError(null);
    try {
      const result = await assessQualitativeMastery({
        bookId,
        knowledgePointId: qualitativeKnowledgePointId,
        answerText,
        questionId,
      });
      setAssessment(result);
      if (result.passed) setHasPassedQualitativeAssessment(true);
    } catch (error) {
      setAssessmentError(t(qualitativeAssessFailureMessage(error)));
    } finally {
      setIsAssessing(false);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--background)] p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <span className="pt-0.5 text-sm font-medium text-[var(--foreground)]">
            {index + 1}.
          </span>
          <div className="min-w-0 flex-1">
            <MarkdownRenderer
              content={String(question.question || t("(missing)"))}
              variant="compact"
              className="font-sans text-sm font-medium text-[var(--foreground)] [&_ol]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p]:my-1.5 [&_pre]:my-2 [&_ul]:my-2"
            />
          </div>
        </div>
        {question.difficulty && (
          <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-[13px] uppercase tracking-wider text-[var(--muted-foreground)]">
            {question.difficulty}
          </span>
        )}
      </div>

      {isQualitativeAssessment ? (
        <div className="mt-3 space-y-2">
          <textarea
            aria-label={t("Your answer")}
            value={answerText}
            onChange={(event) => {
              setAnswerText(event.target.value);
              setAssessment(null);
              setAssessmentError(null);
            }}
            disabled={isAssessing || !questionId}
            rows={5}
            placeholder={t("Write your answer...")}
            className="w-full resize-y rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)] outline-none placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)]/40 disabled:opacity-60"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleQualitativeSubmit()}
              disabled={isAssessing || !questionId}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-[var(--primary-foreground)] disabled:opacity-60"
            >
              {isAssessing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span role="status">{t("Assessing...")}</span>
                </>
              ) : (
                t("Submit for assessment")
              )}
            </button>
          </div>
          {!questionId && (
            <p
              role="alert"
              className="text-xs text-[var(--muted-foreground)]"
            >
              {t("Assessment is unavailable for this question.")}
            </p>
          )}
          {assessmentError && (
            <div
              role="alert"
              className="rounded-lg border border-[var(--border)] bg-[var(--muted)]/40 px-3 py-2 text-sm text-[var(--foreground)]"
            >
              {assessmentError}
            </div>
          )}
          {assessment && (
            <div
              aria-live="polite"
              className="space-y-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2"
            >
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[var(--foreground)]">
                {assessment.passed ? (
                  <CheckCircle2 className="h-4 w-4 text-[var(--primary)]" />
                ) : (
                  <XCircle className="h-4 w-4 text-[var(--muted-foreground)]" />
                )}
                {assessment.passed
                  ? t("Assessment passed")
                  : t("Assessment not yet passed")}
              </div>
              <div>
                <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">
                  {t("Feedback")}
                </div>
                <p className="whitespace-pre-wrap text-sm text-[var(--foreground)]">
                  {assessment.feedback}
                </p>
              </div>
              {assessment.gap !== null && (
                <div>
                  <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">
                    {t("Learning gap")}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-[var(--foreground)]">
                    {assessment.gap}
                  </p>
                </div>
              )}
              <div className="text-xs text-[var(--muted-foreground)]">
                {t("Mastery {{percent}}%", {
                  percent: Math.round(assessment.masteryDisplay * 100),
                })}
              </div>
            </div>
          )}
        </div>
      ) : isChoice && Object.keys(options).length > 0 ? (
        <div className="mt-3 space-y-1.5">
          {Object.entries(options).map(([key, label]) => {
            const upperKey = key.toUpperCase();
            const isSelected = selected === upperKey;
            const isCorrect = revealed && upperKey === correctChoiceKey;
            const isWrongPick =
              revealed && isSelected && upperKey !== correctChoiceKey;
            return (
              <button
                key={key}
                onClick={() => setSelected(upperKey)}
                className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                  isCorrect
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100"
                    : isWrongPick
                      ? "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100"
                      : isSelected
                        ? "border-[var(--primary)] bg-[var(--primary)]/8 text-[var(--foreground)]"
                        : "border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] hover:border-[var(--primary)]/40"
                }`}
              >
                <span className="font-mono text-xs uppercase text-[var(--muted-foreground)]">
                  {upperKey}.
                </span>
                <span className="flex-1 whitespace-pre-wrap break-words">
                  {label}
                </span>
                {isCorrect && <CheckCircle2 className="mt-0.5 h-4 w-4" />}
                {isWrongPick && <XCircle className="mt-0.5 h-4 w-4" />}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-2 text-xs text-[var(--muted-foreground)]">
          {normalizedType === "written"
            ? t("Think about your answer, then reveal the solution.")
            : t("Open response — click reveal to see the model answer.")}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <button
          onClick={() => {
            if (canRevealAnswer) setRevealed((value) => !value);
          }}
          disabled={!canRevealAnswer}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--card)] px-2.5 py-1 text-xs font-medium text-[var(--muted-foreground)] hover:border-[var(--primary)]/40 hover:text-[var(--primary)] disabled:opacity-60"
        >
          {revealed ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
          {revealed ? t("Hide answer") : t("Reveal answer")}
        </button>
        {revealed && correct && isChoice && (
          <span className="text-xs text-[var(--muted-foreground)]">
            {t("Answer")}:{" "}
            <span className="font-mono text-[var(--foreground)]">
              {correctChoiceKey || correct}
            </span>
          </span>
        )}
      </div>

      {revealed &&
        isChoice &&
        selected &&
        selected.toUpperCase() === correctChoiceKey && (
          <div className="mt-2 flex items-center gap-2 rounded-xl bg-success-soft px-3 py-2">
            <img
              src="/mascot/mascot-cheer.png"
              alt=""
              width={36}
              height={36}
              className="dt-float h-9 w-9 shrink-0 select-none"
              draggable={false}
            />
            <span className="text-sm font-semibold text-success-softfg">
              {t("Correct")}!
            </span>
          </div>
        )}

      {revealed && correct && !isChoice && (
        <div className="mt-2 rounded-lg border border-[var(--border)] bg-[var(--card)]/70 p-2">
          <div className="mb-1 text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">
            {t("Answer")}
          </div>
          <MarkdownRenderer
            content={correct}
            variant="compact"
            className="font-sans text-sm text-[var(--foreground)] [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p]:my-1.5 [&_pre]:my-2"
          />
        </div>
      )}

      {revealed && question.explanation && (
        <div className="mt-2 rounded-lg border border-[var(--border)] bg-[var(--muted)]/40 p-2">
          <MarkdownRenderer
            content={String(question.explanation)}
            variant="compact"
          />
        </div>
      )}
    </div>
  );
}
